import { StyleHTMLAttributes } from 'react';
import clsx, { ClassValue } from 'clsx';
import importedDebounce from 'debounce';

import { GlobalProps } from '../types';

export {
  isMoreRecentThan,
  isMoreRecentThan30Minutes,
  isWithinTimePeriod,
} from './date';
export { elementInViewport } from './element';
export { isNumber, isWholeNumber } from './number';
export { pluralize } from './text';
export { throttle } from './throttle';
export { modifyQueryParameters } from './url';

export const NONRESPONSIVE_IMAGES = new Set(['gif', 'png']);

export const DEFAULT_WIDTHS = [
  220, 480, 640, 720, 960, 1020, 1200, 1280, 1400, 1800, 2000,
];

export function slugify(str = ''): string {
  return (
    str
      .replace(/(^[A-Z])/, ([first]) => (first || '').toLowerCase())
      .replace(/([A-Z])/g, ([letter]) => `-${(letter || '').toLowerCase()}`)
      // replace anything that is not a letter A-z or a number 0-9 with a dash
      .replace(/[^A-Z0-9]/gi, '-')
      // get rid of consecutive dashes
      .replace(/-{1,}/g, '-')
      .replace(/^-|-$/g, '')
  );
}

export const createClasses = (...args: ClassValue[]): string => {
  return clsx(...args);
};

// rule for generating stable class names that
// are specified by consumer
export function createClassFromComponentName(name?: string) {
  return name ? `${slugify(name)}` : '';
}

// Main dynamic class generation utility
export const classes = (
  // array of string or objects to conditional create
  // classes. same input as clsx or classNames libraries
  classArr: ClassValue[],
  props?: GlobalProps
): string => {
  // create single string of classes
  const cx = createClasses(
    // if unstyled, nada
    // if not unstyled, pass in remaining classes
    props?.unstyled ? '' : classArr,

    // any classnames consumer passed in. THIS SHOULD BE SECOND
    props?.className || '',

    // create a stable classname. THIS SHOULD BE LAST
    // for prefererred cascade priority
    createClassFromComponentName(props?.name)
  );

  // make sure to drop any uniques if, for example, consumer
  // passed in duplicate classes
  return Array.from(new Set(cx.split(' '))).join(' ');
};

export const optionalStyles = <T = 'HTMLDivElement'>(
  styles: Record<string, string | number | undefined>
): StyleHTMLAttributes<T> => {
  const val = Object.entries(styles).reduce((acc, curr) => {
    const [key, value] = curr;
    const v = styles[key];

    return {
      ...acc,
      ...(v !== undefined &&
        v !== '' && {
          [key]: value,
        }),
    };
  }, {} as StyleHTMLAttributes<T>);

  return val;
};

// turning off rule below as imported lib is using Function type
/* eslint-disable @typescript-eslint/ban-types */
export function debounce<T extends Function>(
  func: T,

  interval?: number,
  immediate?: boolean
) {
  return importedDebounce(func, interval, immediate);
}

export function makeSrcset(
  baseUrl = '',
  nativeWidth: number,
  nativeHeight = -1,
  widthsList: number[] = DEFAULT_WIDTHS,
  srcSetScale = 1,
  useResponsivePNG = false
) {
  const imageExtension = baseUrl.split('.').pop() || '';
  const responsive =
    (useResponsivePNG && imageExtension === 'png') ||
    !NONRESPONSIVE_IMAGES.has(imageExtension);

  if (!baseUrl || !responsive || !widthsList) {
    return '';
  }

  const visited: boolean[] = [];

  return widthsList
    .map((width) => {
      const actualWidth = Math.min(width, nativeWidth);

      if (visited[actualWidth]) {
        return null;
      }

      visited[actualWidth] = true;
      const calculatedHeight =
        nativeHeight > 0
          ? Math.round(nativeHeight * (actualWidth / nativeWidth))
          : -1;

      return baseUrl
        .replace(/^http:\/\//, '//')
        .replace(/^\/\//, 'https://')
        .replace(
          /(\/)(-*\d+x-*\d+)(.\w+)$/,
          `$1${actualWidth * srcSetScale}x${calculatedHeight}$3 ${actualWidth}w`
        );
    })
    .filter(Boolean)
    .join(', ');
}

type Direction = 'neutral' | 'positive' | 'negative';

export const DIRECTIONS = {
  POSITIVE: 'positive',
  NEUTRAL: 'neutral',
  NEGATIVE: 'negative',
} as const;

export function getDirection(change: number): Direction {
  if (change === 0) {
    return 'neutral';
  }

  return change > 0 ? 'positive' : 'negative';
}

export function createId(): string {
  return Math.random().toString(16).slice(2);
}

export function isJapaneseLocale(locale?: string): boolean {
  if (!locale || typeof locale !== 'string') {
    return false;
  }
  const language = locale.split('-')[0];

  return language?.toLowerCase() === 'ja';
}
